import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import {
  ComposerWrap,
  ComposerInner,
  ComposerTabs,
  ComposerTab,
  ComposerTextarea,
  ComposerRow,
  ToolBtn,
  SendBtn,
  UploadingIndicator,
  UploadErrorMsg,
  EmailFieldsWrap,
  EmailFieldRow,
  EmailFieldLabel,
  EmailFieldInput,
  BccToggle,
  SA_COLORS,
} from '../styled-comps';
import Icon from './icon';
import AttachIcon from 'img/paperclip.svg';

// ── Local styled components ───────────────────────────────────────────

const AttachmentPreview = styled(Row)`
  && {
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: ${SA_COLORS.BG_SECONDARY};
    border: 1px solid ${SA_COLORS.BORDER_TERTIARY};
    margin: 0 14px 6px;
    border-radius: 6px;
  }
`;

const AttachmentName = styled.span`
  font-size: 12px;
  color: ${SA_COLORS.TEXT_PRIMARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const RemoveAttachBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${SA_COLORS.TEXT_TERTIARY};
  flex-shrink: 0;

  &:hover {
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const QuoteToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid
    ${props => (props.$active ? SA_COLORS.INFO_600 : SA_COLORS.BORDER_TERTIARY)};
  background: ${props => (props.$active ? SA_COLORS.BG_SECONDARY : 'transparent')};
  cursor: pointer;
  color: ${props => (props.$active ? SA_COLORS.INFO_600 : SA_COLORS.TEXT_SECONDARY)};
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;

  &:hover {
    background: ${SA_COLORS.BG_SECONDARY};
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const ComposerResizeHandle = styled.div`
  width: 100%;
  height: 8px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${SA_COLORS.BG_SECONDARY};
  border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};

  &:hover {
    background: ${SA_COLORS.BORDER_TERTIARY};
  }

  &::before {
    content: '···';
    font-size: 14px;
    line-height: 8px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const QuotedArea = styled.div`
  margin: 0 0 6px 14px;
  padding-top: 8px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.5;
  color: ${SA_COLORS.TEXT_SECONDARY};
  outline: none;

  blockquote {
    margin: 4px 0 4px 0;
    padding-left: 10px;
    border-left: 2px solid #ccc;
  }
`;

const VisibilityMeta = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA_COLORS.TEXT_TERTIARY};
    margin-left: auto;
    padding: 6px 12px 0;
  }
`;

const EmailFieldsComposerWrap = styled(EmailFieldsWrap)`
  padding: 8px 12px 0;
  border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};
`;

// ── Resize helper ─────────────────────────────────────────────────────

const startResize = (e, targetRef, prop, min, max) => {
  e.preventDefault();
  const el = targetRef.current;
  if (!el) return;
  const startY = e.clientY;
  const startH = el.offsetHeight;
  const onMove = ev => {
    el.style[prop] = Math.min(max, Math.max(min, startH + (startY - ev.clientY))) + 'px';
  };
  const onUp = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
};

// ── Component ─────────────────────────────────────────────────────────

const ThreadComposer = ({
  val,
  setVal,
  internal,
  setInternal,
  send,
  handleKeyDown,
  isEmailTicket,
  emailSubject,
  setEmailSubject,
  emailCc,
  setEmailCc,
  emailBcc,
  setEmailBcc,
  showBcc,
  setShowBcc,
  quotedTextIncluded,
  toggleQuotedText,
  quotedAreaRef,
  uploading,
  uploadError,
  attachment,
  setAttachment,
  fileInputRef,
  handleFileSelect,
  customerName,
  perms,
}) => {
  const textareaRef = useRef(null);
  const quotedResizeRef = quotedAreaRef;

  const handleTextareaResize = useCallback(
    e => startResize(e, textareaRef, 'height', 44, 400),
    [],
  );

  const handleQuotedResize = useCallback(
    e => startResize(e, quotedResizeRef, 'maxHeight', 60, 400),
    [quotedResizeRef],
  );

  return (
    <ComposerWrap>
      <ComposerInner>
        <ComposerTabs>
          {perms.can_reply && (
            <ComposerTab $active={!internal} onClick={() => setInternal(false)}>
              Reply to {customerName}
            </ComposerTab>
          )}
          {perms.can_add_note && (
            <ComposerTab
              $active={internal}
              $internal={internal}
              onClick={() => setInternal(true)}
            >
              Internal note
            </ComposerTab>
          )}
          <VisibilityMeta>
            {internal ? 'Only visible to agents' : 'Visible to customer'}
          </VisibilityMeta>
        </ComposerTabs>
        {isEmailTicket && !internal && (
          <EmailFieldsComposerWrap>
            <EmailFieldRow>
              <EmailFieldLabel>Subject</EmailFieldLabel>
              <EmailFieldInput
                name="subject"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                placeholder="Email subject"
              />
            </EmailFieldRow>
            <EmailFieldRow>
              <EmailFieldLabel>CC</EmailFieldLabel>
              <EmailFieldInput
                name="cc"
                value={emailCc}
                onChange={e => setEmailCc(e.target.value)}
                placeholder="Comma-separated emails"
              />
              {!showBcc && <BccToggle onClick={() => setShowBcc(true)}>BCC</BccToggle>}
            </EmailFieldRow>
            {showBcc && (
              <EmailFieldRow>
                <EmailFieldLabel>BCC</EmailFieldLabel>
                <EmailFieldInput
                  name="bcc"
                  value={emailBcc}
                  onChange={e => setEmailBcc(e.target.value)}
                  placeholder="Comma-separated emails"
                />
              </EmailFieldRow>
            )}
          </EmailFieldsComposerWrap>
        )}
        <ComposerResizeHandle onMouseDown={handleTextareaResize} />
        <ComposerTextarea
          ref={textareaRef}
          name="message"
          internalNote={internal}
          placeholder={
            internal ? 'Write an internal note...' : `Reply to ${customerName}...`
          }
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
        />
        {quotedTextIncluded && isEmailTicket && !internal && (
          <>
            <ComposerResizeHandle onMouseDown={handleQuotedResize} />
            <QuotedArea
              ref={quotedAreaRef}
              contentEditable
              suppressContentEditableWarning
            />
          </>
        )}
        {uploading && <UploadingIndicator>Uploading...</UploadingIndicator>}
        {uploadError && <UploadErrorMsg>{uploadError}</UploadErrorMsg>}
        {attachment && (
          <AttachmentPreview>
            <AttachmentName>{attachment.name}</AttachmentName>
            <RemoveAttachBtn onClick={() => setAttachment(null)}>
              <Icon name="close" size={12} />
            </RemoveAttachBtn>
          </AttachmentPreview>
        )}
        <ComposerRow>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
            onChange={handleFileSelect}
          />
          <ToolBtn
            title="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <img src={AttachIcon} alt="Attach" width={14} height={14} />
          </ToolBtn>
          {isEmailTicket && !internal && (
            <QuoteToggleBtn onClick={toggleQuotedText} $active={quotedTextIncluded}>
              {quotedTextIncluded ? 'Remove Quote' : 'Quote Reply'}
            </QuoteToggleBtn>
          )}
          <SendBtn disabled={(!val.trim() && !attachment) || uploading} onClick={send}>
            {uploading ? 'Uploading...' : 'Send'}
          </SendBtn>
        </ComposerRow>
      </ComposerInner>
    </ComposerWrap>
  );
};

export default ThreadComposer;
